import { uuidLocal } from '@/lib/id';
import { useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import type { ProdutoCatalogo } from '@/api';
import type { RootState } from '@/store';
import {
  adicionarItem,
  atualizarQuantidade,
  removerItem,
  type ItemCarrinho,
  type PassoCarrinho,
} from '@/store/carrinho-slice';
import type { ItemPersonalizado } from './dialog-passos';
import { assinaturaItem, montarPassos, precoUnitario, type PassoSelecionado } from './passos';
import { rastrearAdicionouProduto } from '@/lib/tracking';
import { ehBlocoDeAdicionais } from '@/lib/adicional-por-sabor';
import { linhaSimples, useAdicionarProdutoSimples } from '@/lib/use-adicionar-produto-simples'

export interface CarrinhoKiosk {
  itens: ItemCarrinho[];
  totalItens: number;
  subtotal: number;
  quantidadeDoProduto: (idProduto: number) => number;
  adicionarSimples: (produto: ProdutoCatalogo, categoria?: string) => void;
  removerSimples: (produto: ProdutoCatalogo) => void;
  /** Do detalhe do produto (tablet): quantidade e observação escolhidas lá. */
  adicionarComDetalhes: (produto: ProdutoCatalogo, escolha: { quantidade: number; observacao: string }, categoria?: string) => void;
  aplicarPersonalizado: (produto: ProdutoCatalogo, item: ItemPersonalizado, categoria?: string, quantidade?: number) => void;
}

function paraPassoCarrinho(passo: PassoSelecionado): PassoCarrinho {
  return {
    idProdutoPasso: passo.idProdutoPasso,
    descricao: passo.descricao ?? undefined,
    valorPasso: passo.valorPasso,
    ...(ehBlocoDeAdicionais(passo) ? { idProdutoPassoPai: passo.idProdutoPassoPai, idItemPai: passo.idItemPai } : {}),
    itens: passo.itens,
  };
}

export function quantidadeNoCarrinho(itens: ItemCarrinho[], idProduto: number): number {
  return itens.reduce((soma, item) => (item.idProduto === idProduto ? soma + item.quantidade : soma), 0);
}

export function useCarrinhoKiosk(): CarrinhoKiosk {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const itens = useSelector((s: RootState) => s.carrinho.itens);

  const quantidadeDoProduto = useCallback(
    (idProduto: number) => quantidadeNoCarrinho(itens, idProduto),
    [itens],
  );

  const adicionarProdutoSimples = useAdicionarProdutoSimples()

  const adicionarSimples = useCallback(
    (produto: ProdutoCatalogo, categoria?: string) => adicionarProdutoSimples(produto, { categoria }),
    [adicionarProdutoSimples],
  )

  const removerSimples = useCallback(
    (produto: ProdutoCatalogo) => {
      const linha = linhaSimples(store.getState().carrinho.itens, produto.id);
      if (!linha) return;

      if (linha.quantidade <= 1) dispatch(removerItem({ id: linha.id }));
      else dispatch(atualizarQuantidade({ id: linha.id, quantidade: linha.quantidade - 1 }));
    },
    [store, dispatch],
  );

  const adicionarComDetalhes = useCallback(
    (produto: ProdutoCatalogo, { quantidade, observacao }: { quantidade: number; observacao: string }, categoria?: string) => {
      // Mesma observação, mesma linha; observação diferente é outro pedido para a cozinha.
      const igual = store.getState().carrinho.itens.find(
        (i) => i.idProduto === produto.id && !(i.passos?.length ?? 0) && (i.observacao ?? '') === observacao,
      );

      if (igual) {
        dispatch(atualizarQuantidade({ id: igual.id, quantidade: igual.quantidade + quantidade }));
      } else {
        dispatch(
          adicionarItem({
            id: uuidLocal(),
            idProduto: produto.id,
            nome: produto.descricao,
            quantidade,
            precoUnitario: produto.valor,
            faixasPreco: produto.faixasPreco,
            ...(observacao ? { observacao } : {}),
          }),
        );
      }

      rastrearAdicionouProduto({ id: produto.id, nome: produto.descricao, valor: produto.valor, quantidade, categoria });
    },
    [store, dispatch],
  );

  const aplicarPersonalizado = useCallback(
    (produto: ProdutoCatalogo, { selecoes, observacao }: ItemPersonalizado, categoria?: string, quantidade = 1) => {
      const passos = montarPassos(produto, selecoes);
      const unitario = precoUnitario(produto, passos);
      const assinatura = assinaturaItem(produto.id, passos);

      const igual = store.getState().carrinho.itens.find(
        (i) =>
          i.idProduto === produto.id
          && (i.observacao ?? '') === observacao
          && assinaturaItem(i.idProduto, i.passos ?? []) === assinatura,
      );

      if (igual) {
        dispatch(atualizarQuantidade({ id: igual.id, quantidade: igual.quantidade + quantidade }));
      } else {
        dispatch(
          adicionarItem({
            id: uuidLocal(),
            idProduto: produto.id,
            nome: produto.descricao,
            quantidade,
            precoUnitario: unitario,
            observacao: observacao || undefined,
            passos: passos.map(paraPassoCarrinho),
          }),
        );
      }

      rastrearAdicionouProduto({
        id: produto.id,
        nome: produto.descricao,
        valor: unitario,
        quantidade,
        categoria,
      });
    },
    [store, dispatch],
  );

  return {
    itens,
    totalItens: itens.reduce((s, i) => s + i.quantidade, 0),
    subtotal: itens.reduce((s, i) => s + i.precoUnitario * i.quantidade, 0),
    quantidadeDoProduto,
    adicionarSimples,
    removerSimples,
    adicionarComDetalhes,
    aplicarPersonalizado,
  };
}
